import { useCallback, useEffect, useMemo, useState } from 'react';
import { transformDataFromHoaDon } from '../utils/transform-data';
import { useRouter, useSearchParams } from '@/routing';
import type { PhieuNhapKho } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
// import { useTaiKhoan } from '@/hooks/queries/useTaiKhoan';
import api from '@/lib/api';

export function useEditPhieuNhapKhoWithUpdatedInvoice() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { entity } = useAuth();
  const [mergedFormData, setMergedFormData] = useState<PhieuNhapKho | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editUuid = searchParams.get('edit');
  const hdmhUuid = searchParams.get('hdmh');

  const shouldEditWithUpdatedData = useMemo(() => Boolean(editUuid && hdmhUuid), [editUuid, hdmhUuid]);

  const editPhieuNhapKhoUuid = editUuid;

  const fetchInvoiceByUuid = useCallback(
    async (uuid: string) => {
      if (typeof window !== 'undefined') {
        const cachedData = sessionStorage.getItem(`hoa_don_detail_${uuid}`);
        if (cachedData) {
          try {
            return JSON.parse(cachedData);
          } catch (error) {
            // Fall through to API call
          }
        }
      }

      if (!entity?.slug) return null;

      try {
        const response = await api.get(
          `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/${uuid}/`
        );
        return response.data;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  const fetchPhieuNhapKhoByUuid = useCallback(
    async (uuid: string) => {
      if (!entity?.slug) return null;

      try {
        const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_NHAP_KHO}/${uuid}/`);
        return response.data;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  const updatePhieuNhapKhoWithInvoice = useCallback(
    (existingPhieuNhapKho: PhieuNhapKho | null, updatedInvoiceData: PhieuNhapKho | null) => {
      if (!existingPhieuNhapKho || !updatedInvoiceData) {
        return null;
      }

      let transformedFromInvoice = null;
      if (hdmhUuid) {
        transformedFromInvoice = transformDataFromHoaDon(updatedInvoiceData);
      }

      if (!transformedFromInvoice) {
        return existingPhieuNhapKho;
      }

      return {
        ...existingPhieuNhapKho,

        ngay_ct0: updatedInvoiceData.ngay_ct,
        so_ct0: updatedInvoiceData.so_ct,
        dien_giai: updatedInvoiceData.dien_giai,

        ma_kh: updatedInvoiceData.ma_kh_data?.uuid || updatedInvoiceData.ma_kh,
        ong_ba: updatedInvoiceData.ong_ba,
        dia_chi: updatedInvoiceData.dia_chi,

        ma_nt: updatedInvoiceData.ma_nt || 'VND',
        ty_gia: updatedInvoiceData.ty_gia || 1,

        t_tien_nt: updatedInvoiceData.t_tt || 0,
        t_thue_nt: updatedInvoiceData.t_thue_nt || updatedInvoiceData.t_thue || 0,
        t_cp_nt: updatedInvoiceData.t_cp_nt || updatedInvoiceData.t_cp || 0,
        t_tt_nt: updatedInvoiceData.t_tt || 0,
        ngay_ct: updatedInvoiceData.ngay_ct,
        status: updatedInvoiceData.status == '5' ? '8' : updatedInvoiceData.status,

        chi_tiet_data: transformedFromInvoice.chi_tiet_data || existingPhieuNhapKho.chi_tiet_data
      };
    },
    [hdmhUuid]
  );

  useEffect(() => {
    const invoiceUuid = hdmhUuid;

    if (editUuid && invoiceUuid) {
      setIsLoading(true);
      setError(null);

      Promise.all([fetchPhieuNhapKhoByUuid(editUuid), fetchInvoiceByUuid(invoiceUuid)])
        .then(([phieuNhapKhoData, updatedInvoiceData]) => {
          const merged = updatePhieuNhapKhoWithInvoice(phieuNhapKhoData, updatedInvoiceData);
          setMergedFormData(merged);
        })
        .catch(() => {
          setError('Không thể tải dữ liệu phiếu nhập kho hoặc hóa đơn');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setMergedFormData(null);
      setError(null);
    }
  }, [
    editUuid,
    hdmhUuid,
    fetchPhieuNhapKhoByUuid,
    fetchInvoiceByUuid,
    updatePhieuNhapKhoWithInvoice,
    shouldEditWithUpdatedData
  ]);

  const handleCloseEditWithUpdatedData = () => {
    if (hdmhUuid && typeof window !== 'undefined') {
      sessionStorage.removeItem(`hoa_don_detail_${hdmhUuid}`);
    }
    router.back();
  };

  return {
    shouldEditWithUpdatedData,
    mergedFormData,
    editPhieuNhapKhoUuid,
    isLoading,
    error,
    handleCloseEditWithUpdatedData
  };
}
